import { PagePanelColors } from '@/types/page-panel'
import { Question, QuestionTypes } from '@/types/questions'
import { CloseIcon, DictionaryText } from '@lib-components'
import { ReactElement, use, useEffect, useRef, useState } from 'react'
import CheckboxQuestion from '../../form/question-types/CheckboxQuestion'
import FillInTheBlanks from '../../form/question-types/FillInTheBlanks'
import RadioQuestion from '../../form/question-types/RadioQuestion'
import PagePanel from '../../layout/PagePanel'
import { createFormKeyFromActivityAndQuestion } from '@/helpers/registration-check'
import { AdditionalQuestionInformation } from '@/components/registration-check/question-item/_partials/AdditionalQuestionInformation'
import { EndpointNotificationList } from '@/components/registration-check/question-item/_partials/EndpointNotificationList'
import { RegistrationCheckContext } from '@/providers/registration-check-provider'
import { getDepartmentTypeByQuestionId } from '@/components/registration-check/_helpers/getDepartmentTypeByQuestionId'
import { getActivityEndpoints } from '@/components/registration-check/question-item/_helpers/getActivityEndpoints'
import { ActivityContext } from '@/components/registration-check/ActivityItemProvider'

type Props = {
    question: Question
    canBeDeleted: boolean
}

const MESSAGE_KEYS = { REMOVE: 'STO.COMMON.ACTIONS.REMOVE' }

export const QuestionItem = ({ question, canBeDeleted }: Props): ReactElement => {
    const { activity, visibleQuestions, control, formData, deleteActivity } = use(ActivityContext)
    const { endpointsPerFormKey, setEndpointsPerFormKey, questions } = use(RegistrationCheckContext)

    const isVisible = visibleQuestions.includes(question.id)
    const [isQuestionAnswered, setIsQuestionAnswered] = useState(false)
    const [isInfoModalVisible, setInfoModalVisibility] = useState(false)
    const element = useRef(null)
    const activityAndQuestionCombinationKey = createFormKeyFromActivityAndQuestion(activity, question)

    const getPagePanelColor = (): PagePanelColors =>
        !isQuestionAnswered || !element.current?.nextSibling ? PagePanelColors.ACCENT : PagePanelColors.DEFAULT

    useEffect(() => {
        const matchingFormData = formData[activityAndQuestionCombinationKey]
        setIsQuestionAnswered(matchingFormData && matchingFormData !== '')
    }, [formData])

    /**
     * Updates the list of endpoints for a specific activity and question combination,
     * merging new endpoints with existing ones and removing obsolete entries
     */
    const updateEndpointsList = () => {
        const newEndpoints = getActivityEndpoints(
            formData,
            activityAndQuestionCombinationKey,
            question,
            getDepartmentTypeByQuestionId(questions ?? []),
        )
        const existingEndpoints = endpointsPerFormKey[activityAndQuestionCombinationKey]

        if (!existingEndpoints) {
            setEndpointsPerFormKey(activityAndQuestionCombinationKey, newEndpoints)
            return
        }

        // Filter newEndpoints to only include those with endpointKeys not in existingEndpoints
        const uniqueNewEndpoints = newEndpoints.filter(
            (newEndpoint) => !existingEndpoints.some(({ endpointKey }) => endpointKey === newEndpoint.endpointKey),
        )

        //filter out the endpoints that have been removed
        const remainingExistingEndpoints = existingEndpoints.filter(({ endpointKey }) =>
            newEndpoints.some((newEndpoint) => newEndpoint.endpointKey === endpointKey),
        )

        setEndpointsPerFormKey(activityAndQuestionCombinationKey, [
            ...remainingExistingEndpoints,
            ...uniqueNewEndpoints,
        ])
    }

    useEffect(() => {
        updateEndpointsList()
    }, [activity])

    return (
        <>
            {isVisible && (
                <div data-cy="question-row">
                    <PagePanel
                        ref={element}
                        color={getPagePanelColor()}
                        onClickMoreInfo={
                            question.additionalInformation || canBeDeleted
                                ? () => {
                                      canBeDeleted ? deleteActivity() : setInfoModalVisibility(true)
                                  }
                                : null
                        }
                        moreInfoText={canBeDeleted ? <DictionaryText textKey={MESSAGE_KEYS.REMOVE} /> : null}
                        moreInfoIcon={canBeDeleted ? <CloseIcon /> : null}
                        containerClasses="flex gap-2 items-center flex-wrap"
                        className={`question ${isQuestionAnswered ? 'question--answered' : 'question--unanswered'}`}
                    >
                        {question.questionType === QuestionTypes.FILL_IN_THE_BLANKS && (
                            <FillInTheBlanks
                                question={question}
                                questions={questions}
                                activity={activity}
                                control={control}
                                formData={formData}
                            />
                        )}
                        {[QuestionTypes.INLINE_RADIO, QuestionTypes.RADIO].includes(question.questionType) && (
                            <RadioQuestion
                                question={question}
                                name={activityAndQuestionCombinationKey}
                                control={control}
                                inline={question.questionType === QuestionTypes.INLINE_RADIO}
                            />
                        )}
                        {[QuestionTypes.INLINE_CHECKBOX, QuestionTypes.CHECKBOX].includes(question.questionType) && (
                            <CheckboxQuestion
                                question={question}
                                name={activityAndQuestionCombinationKey}
                                control={control}
                                inline={question.questionType === QuestionTypes.INLINE_CHECKBOX}
                            />
                        )}
                    </PagePanel>
                    <AdditionalQuestionInformation
                        question={question}
                        isInfoModalVisible={isInfoModalVisible}
                        setInfoModalVisibility={setInfoModalVisibility}
                    />
                </div>
            )}
            {isQuestionAnswered && (
                <EndpointNotificationList
                    activityAndQuestionCombinationKey={activityAndQuestionCombinationKey}
                    endpoints={endpointsPerFormKey[activityAndQuestionCombinationKey]}
                />
            )}
        </>
    )
}
